import { useState } from "react";
import { toast } from "sonner";
import { Printer, ImageDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusPill, StandingBadge, BrandButton } from "@/components/PageHeader";
import { cedi2 as cedi, fmtDate, showPin, balanceOf, overpaymentOf, errMsg } from "@/lib/api";
import { EditRegistrationButton } from "./EditRegistrationDialog";
import { ResendEmailButton } from "./ResendEmailButton";
import { GateUndoActions } from "@/components/GateUndo";
import { fmtCustom } from "./EventGroup";
import { loadTemplate, downloadStickerImage, stickerData } from "@/components/labels/Sticker";

const StickerButton = ({ r, ev }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const out = await downloadStickerImage(await loadTemplate(), stickerData(r, ev?.title)); toast.success(`Downloaded ${out.name}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <BrandButton onClick={go} disabled={busy} data-testid="regview-download-sticker"><ImageDown className="w-4 h-4" />{busy ? "Preparing…" : "Download sticker"}</BrandButton>;
};

const Row = ({ label, children, testId }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm"><span className="text-gray-500 shrink-0">{label}</span><span className="text-right font-medium break-words min-w-0" data-testid={testId}>{children || "—"}</span></div>
);

const Block = ({ title, children }) => (
  <section className="rounded-lg border border-[#E5E7EB] px-4 py-2"><h3 className="text-[11px] font-bold uppercase tracking-wider text-[#E60000] pt-1 pb-1">{title}</h3><div className="divide-y">{children}</div></section>
);

const Payments = ({ items }) => (
  <Block title="Payment history">
    {items.length ? items.map((p) => (
      <div key={p.id} className={`flex justify-between gap-3 py-1.5 text-xs ${p.type === "refund" ? "text-[#B30000]" : ""}`} data-testid={`regview-payment-${p.receipt_no}`}>
        <span className="font-mono">{p.receipt_no}</span><span>{fmtDate(p.created_at)}</span><span>{p.type === "refund" ? `Refund · ${p.method}` : p.method}</span><span className="font-mono font-semibold">{cedi(p.amount)}</span>
      </div>
    )) : <p className="py-2 text-xs text-gray-500">No payments recorded yet.</p>}
  </Block>
);

const EditHistory = ({ items }) => items.length ? (
  <Block title="Change history">
    <ul className="space-y-2 text-xs" data-testid="regview-edit-history">
      {[...items].reverse().map((h, i) => <li key={i} className="border-l-2 border-[#E60000] pl-2" data-testid={`regview-history-${i}`}>
        <div className="text-gray-500">{fmtDate(h.at)} · {h.by || "Staff"}{h.role ? ` (${h.role})` : ""}</div>
        <div><strong>Reason:</strong> {h.reason}</div>
        <div className="text-gray-600">{h.before} → {h.after}</div>
      </li>)}
    </ul>
  </Block>
) : null;

const Body = ({ r, cf, onChanged, canUndo }) => (
  <div className="space-y-3">
    <Block title="Participant">
      <Row label="Name" testId="regview-name">{r.delegate_name}</Row>
      <Row label="AHPC PIN">{showPin(r.pin)}</Row>
      <Row label="GAMLS ID">{r.gamls_id}</Row>
      <Row label="Staff ID">{r.staff_id}</Row>
      <Row label="Phone">{r.phone}</Row>
      <Row label="Email">{r.email}</Row>
      <Row label="Region">{r.region}</Row>
      <Row label="Standing"><StandingBadge standing={r.standing} /></Row>
      {cf.filter((f) => fmtCustom(r.custom?.[f.key]) !== "").map((f) => <Row key={f.key} label={f.label}>{fmtCustom(r.custom[f.key])}</Row>)}
    </Block>
    <Block title="Event selections">
      <Row label="Attendance">{r.attendance || "In-Person"}</Row>
      <Row label="Category" testId="regview-category">{r.category_name || r.category}</Row>
      <Row label="Souvenir & Dinner">{r.souvenir_dinner?.name}</Row>
      {r.sd_guests?.length > 0 && <Row label="Additional members"><span data-testid="regview-sd-guests">{r.sd_guests.map((g) => `${g.ticket_no} (${g.name})`).join(", ")}</span></Row>}
      <Row label="Accommodation" testId="regview-accommodation">{r.accommodation && `${r.accommodation.hotel_name} · ${r.accommodation.room_name} × ${r.accommodation.nights} night(s)`}</Row>
      <Row label="Registered">{fmtDate(r.created_at)}</Row>
    </Block>
    <Block title="Payment">
      <Row label="Total bill" testId="regview-amount">{cedi(r.amount)}</Row>
      <Row label="Paid">{cedi(r.paid || 0)}</Row>
      {overpaymentOf(r) > 0 ? <Row label="Overpayment">{cedi(overpaymentOf(r))}</Row> : <Row label="Balance">{cedi(balanceOf(r))}</Row>}
      <Row label="Status"><StatusPill status={r.status} /></Row>
    </Block>
    <Payments items={r.payments || []} />
    <EditHistory items={r.edit_history || []} />
    {r.checked_in !== undefined && <Block title="Gate">
      <Row label="Checked in">{r.checked_in ? "Yes" : "No"}</Row>
      <Row label="Kit issued">{r.kit_issued ? "Yes" : "No"}</Row>
      {canUndo && <GateUndoActions txnId={r.id} checkedIn={r.checked_in} kitIssued={r.kit_issued} onDone={onChanged} prefix="regview" />}
    </Block>}
  </div>
);

const PrintHead = ({ r, ev }) => (
  <div className="hidden print:block border-b-2 border-[#E60000] pb-3 mb-4" data-testid="regview-print-head">
    <div className="flex items-center gap-3"><img src="/images/gamls-logo.png" alt="" className="w-12 h-12" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      <div><div className="font-heading font-extrabold text-lg">Ghana Association of Medical Laboratory Scientists</div><div className="text-xs text-gray-600">Registration summary</div></div></div>
    <div className="mt-2 text-sm"><strong>{ev?.title}</strong>{ev?.date && ` · ${ev.date}`}{ev?.venue && ` · ${ev.venue}`}</div>
    <div className="text-xs text-gray-600">Ticket <span className="font-mono font-semibold text-[#E60000]">{r.ticket_no}</span> · Printed {new Date().toLocaleString()}</div>
  </div>
);

export const RegistrationDetailsDialog = ({ r, ev, cf = [], canEdit, canResend = true, actions, onClose, onChanged }) => (
  <Dialog open={!!r} onOpenChange={(o) => { if (!o) onClose(); }}>
    <DialogContent className="regview-dialog max-w-xl max-h-[90vh] overflow-y-auto" data-testid="regview-dialog">
      <DialogHeader><DialogTitle className="flex flex-wrap items-baseline gap-2">Registration details{r?.ticket_no && <span className="font-mono text-sm text-[#E60000]" data-testid="regview-ticket">{r.ticket_no}</span>}</DialogTitle></DialogHeader>
      {r && <div className="print-report" data-testid="regview-document"><PrintHead r={r} ev={ev} /><Body r={r} cf={cf} onChanged={onChanged} canUndo={canResend} /></div>}
      {r && <div className="flex flex-wrap justify-end gap-2 pt-1" data-testid="regview-edit-wrap">
        {actions || <>
          <StickerButton r={r} ev={ev} />
          {canResend && <div className="inline-flex items-center rounded-md border border-[#E5E7EB] px-3 h-10"><ResendEmailButton r={r} onSent={onChanged} testId="regview-resend-email" /></div>}
          <BrandButton variant="outline" onClick={() => window.print()} data-testid="regview-print-button"><Printer className="w-4 h-4" />Print summary</BrandButton>
          {canEdit && <EditRegistrationButton r={r} onChanged={onChanged} />}
        </>}
      </div>}
    </DialogContent>
  </Dialog>
);
